import { nvCartTemplate } from "../assets/templates/cart-template";
import { initCart } from "./initCart";
import { DOMUtils } from "../utils/dom.utils";

var isHeadless = true;
var cartKey: string = 'nvHeadless';
// Track if cart has already been loaded to prevent duplicate renders
let cartLoaded = false;
// Retry mount if called before mount element exists
let mountRetryTimer: number | null = null;
let mountRetryAttempts = 0;
let pendingOptions: any | null = null;
const mountRetryMaxAttempts = 20;
const mountRetryIntervalMs = 200;

export const load = function (triggerObj: any) {
    // Prevent duplicate loading
    if (cartLoaded) {
        console.warn('[nvCart] load skipped: cartLoaded=true');
        return;
    }
    
    // Validate triggerObj before proceeding
    if (!triggerObj) {
        console.warn('[nvCart] load skipped: triggerObj missing');
        return;
    }
    if (!triggerObj.mountId) {
        console.warn('[nvCart] load skipped: mountId missing');
        return;
    }

    // Store triggerObj in a local variable to ensure it's captured in the async callback
    const options = triggerObj;

    if (window.location !== window.parent.location) {
        // The page is in an iframe
        console.warn('[nvCart] load skipped: running in iframe');
        return false;
    } else {
        // The page is not in an iframe
        // Inject cart template into mount points BEFORE mounting
        const mountElements = DOMUtils.querySelectorAll('.' + options.mountId);
        if (mountElements.length === 0) {
            console.warn('[nvCart] load deferred: mount element not found', options.mountId);
            pendingOptions = options;
            if (mountRetryTimer == null) {
                mountRetryAttempts = 0;
                const retry = () => {
                    if (cartLoaded || !pendingOptions) {
                        mountRetryTimer = null;
                        pendingOptions = null;
                        return;
                    }
                    const retryMountElements = DOMUtils.querySelectorAll('.' + pendingOptions.mountId);
                    if (retryMountElements.length === 0) {
                        mountRetryAttempts++;
                        if (mountRetryAttempts >= mountRetryMaxAttempts) {
                            mountRetryTimer = null;
                            pendingOptions = null;
                            return;
                        }
                        mountRetryTimer = window.setTimeout(retry, mountRetryIntervalMs);
                        return;
                    }
                    mountRetryTimer = null;
                    const optionsToLoad = pendingOptions;
                    pendingOptions = null;
                    load(optionsToLoad);
                };
                mountRetryTimer = window.setTimeout(retry, mountRetryIntervalMs);
            }
            return;
        }
        // Mark as loaded only after mount elements are confirmed
        cartLoaded = true;

        // Generate template HTML
        const templateHTML = nvCartTemplate.generate();
        
        // Extract and insert only the cart icon HTML (badge + link) into mount elements FIRST
        // Create a temporary container to parse the template
        const tempContainer = DOMUtils.createElement('div');
        tempContainer.innerHTML = templateHTML;
        
        // Get the cart icon elements (badge and link), cart-blocker, and cart-main-section
        const badge = tempContainer.querySelector('.nav-badge');
        const link = tempContainer.querySelector('.nav-cart-link');
        const cartBlocker = tempContainer.querySelector('.cart-blocker');
        const cartMainSection = tempContainer.querySelector('.cart-main-section');
        
        // Insert cart icon + cart-main-section into mount elements (only if not already present)
        mountElements.forEach((element, index) => {
            // Check if cart icon already exists in this mount element
            const existingBadge = element.querySelector('.nav-badge');
            const existingLink = element.querySelector('.nav-cart-link');
            const existingCartSection = element.querySelector('.cart-main-section');
            
            if (existingBadge && existingLink && existingCartSection) {
                return;
            }
            
            if (badge && link && cartMainSection) {
                // IMPORTANT: Completely clear the mount element first to remove any pre-existing HTML
                // This prevents duplicate cart links from persisting
                element.innerHTML = '';
                
                // Clone elements to avoid removing them from tempContainer
                const clonedBadge = badge.cloneNode(true) as HTMLElement;
                const clonedLink = link.cloneNode(true) as HTMLElement;
                const clonedCartBlocker = cartBlocker ? (cartBlocker.cloneNode(true) as HTMLElement) : null;
                const clonedCartSection = cartMainSection.cloneNode(true) as HTMLElement;
                
                // Append elements one by one using DOM methods (not string concatenation)
                // This ensures proper DOM structure and prevents browser HTML parser issues
                element.appendChild(clonedBadge);
                element.appendChild(clonedLink);
                if (clonedCartBlocker) {
                    element.appendChild(clonedCartBlocker);
                }
                element.appendChild(clonedCartSection);
            }
        });
        
        // Remove cart icon HTML, cart-blocker, and cart-main-section from template before appending to body
        // This prevents duplicate cart elements
        if (badge && link) {
            badge.remove();
            link.remove();
        }
        if (cartBlocker) {
            cartBlocker.remove();
        }
        if (cartMainSection) {
            cartMainSection.remove();
        }
        
        // Append styles and Handlebars script to body (without cart icon and cart-main-section)
        // This ensures the #nv-cartTemplate script is available for initCart.ts
        DOMUtils.insertAdjacentHTML(document.body, 'beforeend', tempContainer.innerHTML);
        
        // Verify script tag is in DOM
        const scriptTag = DOMUtils.querySelector('#nv-cartTemplate');

        // Ensure DOM is updated before initializing cart
        requestAnimationFrame(async () => {
            // Initialize cart with the options after template is appended
            await initCart(options, cartKey, isHeadless);
        });
    }

};